import * as React from "react";
import { toast } from "sonner";
import { CashierSessionHeader } from "./CashierSessionHeader";
import { CashierCategoryTabs } from "./CashierCategoryTabs";
import { CashierCatalogGrid } from "./CashierCatalogGrid";
import { CashierCartPanel } from "./CashierCartPanel";
import { CashierTenderPanel } from "./CashierTenderPanel";
import { CashierDiscountSheet } from "./CashierDiscountSheet";
import { CashierParkedDrawer } from "./CashierParkedDrawer";
import { CashierSkeleton, CashierErrorState, CashierSessionClosedState } from "./CashierStates";
import { addItemToOrder, recomputeOrder, removeLine, updateLineQty } from "./utils";
import type {
  CashierLabels, CashierOrder, CashierOrderLineDiscount, CashierSnapshot, CashierTender,
  CashierCatalogIcon, CashierCatalogItem,
} from "./types";
import { cn } from "@/lib/utils";

export interface CashierViewProps {
  snapshot: CashierSnapshot;
  labels: CashierLabels;
  customers?: readonly { id: string; name: string; petId?: string | null; petName?: string | null }[];
  isLoading?: boolean;
  error?: { message: string } | null;
  onRetry?: () => void;
  onAddItem?: (item: CashierCatalogItem, qty: number) => void;
  onRemoveLineItem?: (lineId: string) => void;
  onUpdateQty?: (lineId: string, qty: number) => void;
  onApplyDiscount?: (lineId: string | null, discount: CashierOrderLineDiscount) => void;
  onSelectCustomer?: (customer: { id: string; name: string; petId?: string | null; petName?: string | null }) => void;
  onCreateCustomer?: () => void;
  onParkOrder?: (label: string) => void;
  onResumeParked?: (orderId: string) => void;
  onVoidOrder?: (reason?: string) => void;
  onOpenTender?: () => void;
  onCommitTender?: (tenders: CashierTender[]) => Promise<void> | void;
  onCloseSession?: () => void;
  /**
   * PeToTa drift vs. Lovable source:
   * When true, the internal `CashierTenderPanel` is suppressed and only the
   * external `onOpenTender` callback is fired. Host app is expected to present
   * its own provider-backed payment dialog (e.g. B1.8 PaymentDialog with 3DS).
   */
  externalTenderControl?: boolean;
  locale?: "tr" | "en";
  className?: string;
  dataTestId?: string;
}

export function CashierView({
  snapshot, labels, customers = [], isLoading, error, onRetry,
  onAddItem, onRemoveLineItem, onUpdateQty, onApplyDiscount,
  onSelectCustomer, onCreateCustomer, onParkOrder, onResumeParked, onVoidOrder,
  onOpenTender, onCommitTender, onCloseSession,
  externalTenderControl = false,
  locale = "tr", className, dataTestId,
}: CashierViewProps) {
  const [workingOrder, setWorkingOrder] = React.useState<CashierOrder>(snapshot.activeOrder);
  const [parkedList, setParkedList] = React.useState(snapshot.parkedOrders);
  const [activeCategory, setActiveCategory] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [tenderOpen, setTenderOpen] = React.useState(false);
  const [parkedOpen, setParkedOpen] = React.useState(false);
  const [discountTarget, setDiscountTarget] = React.useState<{ lineId: string | null; lineLabel?: string } | null>(null);

  React.useEffect(() => {
    setWorkingOrder(snapshot.activeOrder);
    setParkedList(snapshot.parkedOrders);
  }, [snapshot.activeOrder.id, snapshot.parkedOrders.length]);

  const categoryIconMap = React.useMemo<Record<string, CashierCatalogIcon>>(() => {
    const m: Record<string, CashierCatalogIcon> = {};
    for (const c of snapshot.categories) m[c.key] = c.icon;
    return m;
  }, [snapshot.categories]);

  const filteredItems = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return snapshot.catalog.filter((it) => {
      if (activeCategory && it.categoryKey !== activeCategory) return false;
      if (!q) return true;
      return (
        it.label.toLowerCase().includes(q) ||
        (it.sku?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [snapshot.catalog, activeCategory, searchQuery]);

  const handleAdd = (item: CashierCatalogItem) => {
    setWorkingOrder((o) => addItemToOrder(o, item, 1));
    onAddItem?.(item, 1);
  };

  const handleQty = (lineId: string, qty: number) => {
    setWorkingOrder((o) => updateLineQty(o, lineId, qty));
    onUpdateQty?.(lineId, qty);
  };

  const handleRemove = (lineId: string) => {
    setWorkingOrder((o) => removeLine(o, lineId));
    onRemoveLineItem?.(lineId);
  };

  const applyDiscount = (lineId: string | null, discount: CashierOrderLineDiscount) => {
    setWorkingOrder((o) => {
      let next = o;
      if (lineId) {
        const line = o.lines.find((l) => l.lineId === lineId);
        if (!line) return o;
        const baseTotal = line.qty * line.unitPriceMinor;
        const valueMinor = discount.kind === "percent"
          ? Math.round(baseTotal * (discount.valueMinor / 100))
          : discount.valueMinor;
        next = recomputeOrder({
          ...o,
          lines: o.lines.map((l) =>
            l.lineId === lineId
              ? { ...l, discount: { ...discount, valueMinor } }
              : l,
          ),
        });
      } else {
        const valueMinor = discount.kind === "percent"
          ? Math.round(o.subtotalMinor * (discount.valueMinor / 100))
          : discount.valueMinor;
        next = recomputeOrder({ ...o, orderDiscountMinor: valueMinor });
      }
      return next;
    });
    onApplyDiscount?.(lineId, discount);
    setDiscountTarget(null);
    toast.success(labels.discount.apply);
  };

  const handlePark = () => {
    if (workingOrder.lines.length === 0) return;
    const label =
      typeof window !== "undefined"
        ? window.prompt(labels.cart.parkPrompt, workingOrder.parkedLabel ?? workingOrder.customerName ?? "")
        : null;
    const finalLabel = label ?? workingOrder.customerName ?? labels.parked.unnamed;
    setParkedList((p) => [{ ...workingOrder, status: "parked", parkedLabel: finalLabel, openedAt: new Date().toISOString() }, ...p]);
    setWorkingOrder({
      ...snapshot.activeOrder,
      id: `order-${Date.now()}`,
      lines: [], subtotalMinor: 0, taxMinor: 0, totalMinor: 0, orderDiscountMinor: 0,
      tenders: [], status: "open", openedAt: new Date().toISOString(),
      parkedLabel: null, customerId: null, customerName: null, petId: null, petName: null,
    });
    onParkOrder?.(finalLabel);
    toast.success(labels.cart.park);
  };

  const handleResume = (orderId: string) => {
    const o = parkedList.find((x) => x.id === orderId);
    if (!o) return;
    if (workingOrder.lines.length > 0) {
      setParkedList((p) => [
        { ...workingOrder, status: "parked", parkedLabel: workingOrder.customerName ?? labels.parked.unnamed },
        ...p.filter((x) => x.id !== orderId),
      ]);
    } else {
      setParkedList((p) => p.filter((x) => x.id !== orderId));
    }
    setWorkingOrder({ ...o, status: "open" });
    setParkedOpen(false);
    onResumeParked?.(orderId);
    toast(labels.parked.resume);
  };

  const handleVoidParked = (orderId: string) => {
    setParkedList((p) => p.filter((x) => x.id !== orderId));
  };

  const handleVoid = () => {
    if (workingOrder.lines.length === 0) return;
    if (typeof window !== "undefined" && !window.confirm(labels.cart.voidConfirm)) return;
    setWorkingOrder((o) => recomputeOrder({ ...o, lines: [], orderDiscountMinor: 0 }));
    onVoidOrder?.();
  };

  const handleOpenTender = () => {
    if (!externalTenderControl) setTenderOpen(true);
    onOpenTender?.();
  };

  const handleCommitTender = async (tenders: CashierTender[]) => {
    await onCommitTender?.(tenders);
    setWorkingOrder((o) => ({
      ...o, id: `order-${Date.now()}`, lines: [],
      subtotalMinor: 0, taxMinor: 0, totalMinor: 0, orderDiscountMinor: 0,
      tenders: [], status: "open", openedAt: new Date().toISOString(),
      customerId: null, customerName: null, petId: null, petName: null,
    }));
    toast.success(labels.tender.commitSuccess);
  };

  if (isLoading) return <CashierSkeleton />;
  if (error) return <CashierErrorState labels={labels} onRetry={onRetry} message={error.message} />;
  if (snapshot.session.status !== "open") return <CashierSessionClosedState labels={labels} />;

  return (
    <div
      data-testid={dataTestId}
      className={cn("h-full flex flex-col bg-biz-bg", className)}
    >
      <CashierSessionHeader
        session={snapshot.session}
        labels={labels}
        branchName={snapshot.branchName}
        parkedCount={parkedList.length}
        onCloseSession={onCloseSession}
        onOpenParkedDrawer={() => setParkedOpen(true)}
        locale={locale}
      />

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] xl:grid-cols-[minmax(0,1fr)_440px]">
        <section className="flex flex-col min-h-0 min-w-0">
          <CashierCategoryTabs
            categories={snapshot.categories}
            activeCategoryKey={activeCategory}
            onCategoryChange={setActiveCategory}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            labels={labels}
          />
          <div className="flex-1 min-h-0">
            <CashierCatalogGrid
              items={filteredItems}
              categoryIconMap={categoryIconMap}
              onAddItem={handleAdd}
              emptyLabel={searchQuery ? labels.catalog.noResults : labels.catalog.emptyCategory}
              labels={labels}
              locale={locale}
            />
          </div>
        </section>

        <CashierCartPanel
          order={workingOrder}
          labels={labels}
          customers={customers}
          locale={locale}
          onSelectCustomer={(c) => {
            setWorkingOrder((o) => ({ ...o, customerId: c.id, customerName: c.name, petId: c.petId ?? null, petName: c.petName ?? null }));
            onSelectCustomer?.(c);
          }}
          onClearCustomer={() => setWorkingOrder((o) => ({ ...o, customerId: null, customerName: null, petId: null, petName: null }))}
          onCreateCustomer={onCreateCustomer}
          onRemoveLineItem={handleRemove}
          onUpdateQty={handleQty}
          onApplyDiscount={(lineId) => {
            const ln = lineId ? workingOrder.lines.find((l) => l.lineId === lineId) : null;
            setDiscountTarget({ lineId, lineLabel: ln?.label });
          }}
          onPark={handlePark}
          onVoid={handleVoid}
          onOpenTender={handleOpenTender}
        />
      </div>

      <CashierDiscountSheet
        isOpen={discountTarget !== null}
        onClose={() => setDiscountTarget(null)}
        onApply={(d) => applyDiscount(discountTarget?.lineId ?? null, d)}
        labels={labels}
        lineLabel={discountTarget?.lineLabel}
      />

      {!externalTenderControl && (
        <CashierTenderPanel
          isOpen={tenderOpen}
          onClose={() => setTenderOpen(false)}
          totalMinor={workingOrder.totalMinor}
          availableTenders={snapshot.availableTenders}
          onCommit={handleCommitTender}
          labels={labels}
          locale={locale}
        />
      )}

      <CashierParkedDrawer
        isOpen={parkedOpen}
        onClose={() => setParkedOpen(false)}
        parkedOrders={parkedList}
        onResume={handleResume}
        onVoid={handleVoidParked}
        labels={labels}
        locale={locale}
      />
    </div>
  );
}
